import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import XtractTable from 'GeneralUI/XtractTable/XtractTable.jsx'
import SearchInput from 'react-search-input'
import { deepEqual } from 'Helper/deepEqual.js'

const SearchList = ({
  exclude = null,
  initSelection,
  columns,
  selected: propsSelected,
  clearOnSelect = false,
  keepSearchOnSelect = false,
  data: propsData,
  newObject,
  selectAllOnClick,
  clickHandler = () => null,
  alwaysShowList: showList = false,
  placeholder,
  style,
  readOnly,
  sortable
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState([])
  const [sorting, setSorting] = useState([])
  const [rowSelection, setRowSelection] = useState({})
  const [rowToScrollTo, setRowToScrollTo] = useState(false)

  useEffect(() => {
    if (typeof initSelection === 'object') {
      const initObject = initSelection
      const initTerm = initObject[columns[0].id]
      setSearchTerm(initTerm)
      setSelected(initObject)
    }
  }, [])

  useEffect(() => {
    if (propsSelected !== undefined) {
      setSelected(propsSelected)
    }
  }, [propsSelected])

  useEffect(() => {
    if (rowSelection !== undefined) {
      if (rowToScrollTo) {
        setRowToScrollTo(false)
        scrollSelectionToMiddle()
      }
    }
  }, [rowSelection])

  useEffect(() => {
    // need to find the selected item and update the row selection for XtractTable
    const data = getFilteredObjects()
    const selectedIndex = data.findIndex(item => deepEqual(item, selected))
    const nextRowSelection = {}
    if (selectedIndex >= 0) {
      nextRowSelection[selectedIndex] = true
      setRowToScrollTo(true)
    }

    // Only update rowSelection when the actual selection changes. This effect
    // runs on every render (because `getFilteredObjects` is redefined each
    // render), so without this guard we would set a new empty object every
    // render, causing both an infinite render churn and, more importantly,
    // thrashing the controlled `rowSelection` prop passed to XtractTable,
    // which leaves TanStack's row-click handler unable to reliably propagate
    // a subsequent click on the "New" sentinel row after a save/update.
    if (!deepEqual(rowSelection, nextRowSelection)) {
      setRowSelection(nextRowSelection)
    }
  }, [selected, getFilteredObjects])

  const scrollSelectionToMiddle = () => {
    // if the selected row is not in view, move it to the center of the table element

    const table = document.querySelector('.xtract-table .table-div')
    const selectedRow = document.querySelector('.search-results .selected-row')

    // If there is no selected row (e.g. after the parent cleared the
    // selection via Cancel), there's nothing to scroll to.
    if (!table || !selectedRow) {
      return
    }

    const topPosOfSelectedRow =
      selectedRow.offsetTop - (selectedRow.scrollHeight + 1) - table.scrollTop
    const topPosOfBottomRow =
      table.clientHeight - 2 * (selectedRow.scrollHeight + 3)

    if (topPosOfSelectedRow < 0 || topPosOfSelectedRow > topPosOfBottomRow) {
      // Calculate the offset of the selected row relative to the table
      const rowOffset = selectedRow.offsetTop - table.offsetTop

      // Calculate the desired scroll position to center the row
      const scrollPosition = rowOffset - table.clientHeight / 3

      // Set the table's scroll position to center the selected row
      table.scrollTop = scrollPosition
    }
  }

  const objectToSearch = object => {
    if (!object) return ''
    let searchTerm = ''
    const columnNames = columns.map(config => {
      return config.id
    })
    columnNames.forEach(column => {
      if (object[column]) {
        searchTerm += (searchTerm ? ' ' : '') + object[column]
      }
    })
    return searchTerm
  }
  const selectObject = object => {
    // When keepSearchOnSelect is set, leave the search box untouched so the
    // list stays filtered to the typed term after a selection — the user
    // clears the search themselves. Otherwise fall back to the clearOnSelect
    // (empty box) / object-name behavior.
    if (!keepSearchOnSelect) {
      const newSearchTerm = clearOnSelect ? '' : objectToSearch(object)
      setSearchTerm(newSearchTerm)
    }
    setSelected(object)
  }
  const searchUpdated = term => {
    const newSelected = { ...selected }
    const newSearchTerm = objectToSearch(newSelected)
    if (term !== newSearchTerm || term === '') {
      setSearchTerm(term)
      // Only clear selection if the user is actively typing (i.e. term is non-empty and doesn't match selected item)
      if (term !== '' && term !== newSearchTerm) {
      setSelected(null)
      }
    }
  }
  const listClick = object => {
    clickHandler(object)
    selectObject(object)
  }
  const filterList = (target, list, columnNames) => {
    const terms = target.toLowerCase().split(' ')
    return list.filter(item => {
      return columnNames.some(columnName => {
        return terms.every(term => {
          return (
            item[columnName] !== undefined &&
            item[columnName].toString().toLowerCase().includes(term)
          )
        })
      })
    })
  }

  const compareNoCase = (sortSpec, a, b) => {
    return (a, b) => {
      // case insensitive sort, or numeric
      let valA = a[sortSpec.id]
      if (typeof valA === 'string') {
        valA = valA.toUpperCase()
      }
      let valB = b[sortSpec.id]
      if (typeof valB === 'string') {
        valB = valB.toUpperCase()
      }
      const sign = sortSpec.desc ? -1 : 1
      const result = valA < valB ? -1 : valA > valB ? 1 : 0
      return sign * result
    }
  }

  const sortList = data => {
    let workingData = data.slice()

    sorting.forEach(sortSpec => {
      const column = columns.find(column => column.id === sortSpec.id)
      const sortFn = column.sortFn?.() || compareNoCase(sortSpec)
      workingData = workingData.sort(sortFn)
    })

    return workingData
  }
  const getFilteredObjects = () => {
    const columnNames = columns.map(config => {
      return config.id
    })
    const filteredObjects =
      (!selected || clearOnSelect || keepSearchOnSelect) && propsData
        ? filterList(searchTerm, propsData, columnNames)
        : []

    let sortedData = sortList(filteredObjects)

    // if we are updating the object, check to see if there should be a "create new" in the list
    if (newObject != null) {
      newObject = { ...newObject }
      // push to beginning of array
      sortedData.unshift(newObject)
    }

    if (exclude != null) {
      // Remove any filteredObjects that have a match in the optional exclude array
      sortedData = sortedData.filter(object => {
        return !exclude.some(excludeObject => {
          // See if every column name we are configured for is a match between the exclude object
          // and the object being filtered
          return columnNames.every(index => {
            return excludeObject[index] === object[index]
          })
        })
      })
    }

    return sortedData
  }
  const onFocus = e => {
    if (selectAllOnClick) {
      setTimeout(
        e => {
          e.setSelectionRange(0, e.value.length)
        },
        100,
        e.target
      )
    }
  }

  const readOnlyStyle = readOnly ? { cursor: 'not-allowed' } : {}
  const data = getFilteredObjects()

  return (
    <div className="search-container" style={style}>
      <SearchInput
        aria-label={`${placeholder} search input`}
        className="search-input"
        readOnly={readOnly || false}
        value={searchTerm}
        onChange={searchUpdated}
        onFocus={onFocus}
        placeholder={placeholder}
        style={readOnlyStyle}
      />
      {(selected == null && searchTerm !== '') || showList ? (
        <div className="search-results">
          <XtractTable
            showControls={false}
            showPagination={false}
            enableSorting={sortable !== false}
            showTinyColumnChooser={false}
            data={data}
            columns={columns}
            manualSorting={true}
            className={'-highlight'}
            pagination={{
              pageIndex: 0,
              pageSize: Math.max(4, data.length)
            }}
            rowOnClickAction={'single-select-sticky'}
            onSortingChange={sorting => setSorting(sorting)}
            rowSelection={rowSelection}
            onRowSelectionChange={rowSelection => {
              const rowIndexes = Object.keys(rowSelection).map(index =>
                parseInt(index)
              )
              const row = rowIndexes.length ? data[rowIndexes[0]] : null
              // Always propagate the selection change to the parent.
              // The XtractTable's 'single-select-sticky' rowOnClickAction
              // already prevents re-firing when the user clicks an
              // already-selected row, so we don't need an additional guard
              // here. A stale-state guard (previously tracked via
              // prevSelected) caused clicks on the "New" sentinel row to be
              // silently dropped after save/update flows.
              listClick(row)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

SearchList.propTypes = {
  // used to configure the table of results. Typically this will be an array of 1
  // but in theory you might want multiple columns in your results (ex first,last,mi)
  // which would result in an array of 3 items
  columns: PropTypes.arrayOf(
    // Object configuring the column of the table
    PropTypes.shape({
      // The name of the field in the supplied array of data objects to display
      // in this column
      id: PropTypes.string.isRequired,
      // The name at the top of the column
      header: PropTypes.string.isRequired
    })
  ).isRequired,
  // When a row is clicked, should it clear the text in the search box?
  clearOnSelect: PropTypes.bool,
  // When a row is clicked, keep the typed search term so the list stays
  // filtered until the user clears the search themselves. Takes precedence
  // over clearOnSelect for the search-box text.
  keepSearchOnSelect: PropTypes.bool,
  // A function that receives the data object associated with a clicked search row
  clickHandler: PropTypes.func.isRequired,
  // The array of data being searched
  data: PropTypes.arrayOf(
    // One or more objects being searched. Must have all properties referenced by
    // id in the columns array prop
    PropTypes.object.isRequired
  ),
  // Should the result list always be visible (ex manage user screen) or only during
  // searching (ex patient search)
  alwaysShowList: PropTypes.bool,
  // Should we 'select all' the value when clicked?
  selectAllOnClick: PropTypes.bool,
  // Place holder text to put in the search box
  placeholder: PropTypes.string,
  // Optional styling for the search results
  style: PropTypes.object,
  // Initial selection value
  initSelection: PropTypes.object,
  // List of values to exclude
  exclude: PropTypes.array,
  // Should this control be read-only?
  readOnly: PropTypes.bool
}

export default SearchList
